import EventIcon from "@mui/icons-material/Event";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SummarizeIcon from "@mui/icons-material/Summarize";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  ListItem,
  Rating,
  Tooltip,
  Typography,
  useTheme,
  Avatar,
  LinearProgress,
} from "@mui/material";
import moment from "moment";
import { useQuery } from "react-query";
import {
  Cell,
  Tooltip as NewTip,
  Pie,
  PieChart,
  ResponsiveContainer,
} from "recharts";

import { useNavigate } from "react-router-dom";
import {
  getClinicianProfile,
  getDashboardData,
  getSessions,
} from "../../../api/services/api";
import AnalyticsCard from "../../../components/AnalyticsCard";
import ColorTags from "../../../components/ColorTags";
import Loader from "../../../components/Loader";
import textCapitalization from "../../../components/textCapitalization";
import { AvatarPrimary, BoxItemWrapper, DotLegend, TitleText } from "./style";

function TotalAnalytics() {
  const theme = useTheme();
  // const [status] = useState(["PENDING", "APPROVED", "PAID"]);

  const { data, isLoading } = useQuery(
    ["clinician-profile"],
    getClinicianProfile
  );
  const navigate = useNavigate();
  const clinicianId = data?.data?.id;

  const { data: dashboardData, isLoading: DashboadLoading } = useQuery(
    ["dashboard-data", data?.data?.id],
    getDashboardData,
    {
      enabled: Boolean(data?.data?.id),
    }
  );

  const { data: allSessionData } = useQuery(
    [
      "sessions",
      {
        clinicianId,
      },
    ],
    getSessions
  );

  const numberOfPaymentsFromAllSessions =
    allSessionData?.data?.sessions?.filter(
      (session: any) => session.sessionPayment?.paymentStatus === true
    ).length || 0;

  const COLORS = ["#FFA319", "#57CA22", "#0052CC"];

  const customStyles = {
    background1: "rgba(128, 128, 128, 0.6)",
    background2: "rgba(204, 119, 34, 0.6)",
    borderRadius: "18px 18px 0px 0px",
    opacity: 0.1,
  };

  const currentDate = new Date();
  const currentMonth = currentDate?.toISOString()?.slice(0, 7);

  const previousMonthDate = new Date(currentDate);
  previousMonthDate.setMonth(currentDate?.getMonth() - 1);
  const previousMonth = previousMonthDate?.toISOString()?.slice(0, 7);

  const currentMonthSessions = allSessionData?.data?.sessions?.filter(
    (f: any) => {
      const sessionMonth = f?.date?.slice(0, 7);
      return sessionMonth === currentMonth;
    }
  );

  const previousMonthSessions = allSessionData?.data?.sessions?.filter(
    (f: any) => {
      const sessionMonth = f?.date?.slice(0, 7);
      return sessionMonth === previousMonth;
    }
  );

  const numberOfSessionsPercentageChange = calculateSessionPercentageChange(
    currentMonthSessions,
    previousMonthSessions
  );
  const numberOfPaymentsPercentageChange = calculatePaymentPercentageChange(
    currentMonthSessions,
    previousMonthSessions
  );
  const totalClientsPercentageChange = calculateClientPercentageChange(
    currentMonthSessions,
    previousMonthSessions
  );

  if (isLoading || DashboadLoading) {
    return <Loader />;
  }

  const totalSessions = dashboardData?.data?.sessionDetails?.total || 0;
  const completionRate = totalSessions > 0
    ? Math.round(((dashboardData?.data?.sessionDetails?.completed || 0) / totalSessions) * 100)
    : 0;

  const statsCards = [
    {
      title: "Total Sessions",
      count: totalSessions,
      icon: <WatchLaterIcon sx={{ fontSize: 40 }} />,
      color: theme.colors.primary,
      route: "/sessions",
      change: numberOfSessionsPercentageChange,
      bgGradient: `linear-gradient(135deg, ${theme.colors.primary.lighter} 0%, ${theme.colors.primary.light} 100%)`,
    },
    {
      title: "Total Payments",
      count: numberOfPaymentsFromAllSessions,
      icon: <SummarizeIcon sx={{ fontSize: 40 }} />,
      color: theme.colors.warning,
      route: "/payments",
      change: numberOfPaymentsPercentageChange,
      bgGradient: `linear-gradient(135deg, ${theme.colors.warning.lighter} 0%, ${theme.colors.warning.light} 100%)`,
    },
    {
      title: "Total Clients",
      count: dashboardData?.data?.totalPatients || 0,
      icon: <PersonAddIcon sx={{ fontSize: 40 }} />,
      color: theme.colors.success,
      route: "/patients",
      change: totalClientsPercentageChange,
      bgGradient: `linear-gradient(135deg, ${theme.colors.success.lighter} 0%, ${theme.colors.success.light} 100%)`,
    },
  ];

  return (
    <>
      <Box my={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h2" gutterBottom>
            Dashboard Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's your practice summary for {moment().format("MMMM YYYY")}
          </Typography>
        </Box>
        <Chip
          label={`${completionRate}% Completion Rate`}
          sx={{
            fontSize: 16,
            fontWeight: 600,
            px: 2,
            py: 3,
            backgroundColor: theme.colors.success.main,
            color: "white"
          }}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={9}>
          <Grid container spacing={2}>
            {statsCards.map((stat, index) => (
              <Grid item lg={4} md={6} key={index}>
                <Card
                  onClick={() => navigate(stat.route)}
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Box sx={{ background: stat.bgGradient, p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: "white",
                          color: stat.color.main,
                          width: 56,
                          height: 56,
                          boxShadow: 2,
                        }}
                      >
                        {stat.icon}
                      </Avatar>
                      {stat.change !== null && stat.change !== undefined && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {Number(stat.change) >= 0 ? (
                            <TrendingUpIcon sx={{ color: theme.colors.success.main, fontSize: 20 }} />
                          ) : (
                            <TrendingDownIcon sx={{ color: theme.colors.error.main, fontSize: 20 }} />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: Number(stat.change) >= 0 ? theme.colors.success.main : theme.colors.error.main
                            }}
                          >
                            {Math.abs(Number(stat.change))}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: stat.color.main, mb: 0.5 }}>
                      {stat.count}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.colors.alpha.black[100] }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.alpha.black[70] }}>
                      This month
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card sx={{ width: "100%", marginTop: "20px", marginBottom: "20px", boxShadow: 3 }}>
            <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Upcoming Sessions
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate("/sessions/upcoming-sessions")}
              >
                View All
              </Button>
            </Box>
            <Divider />
            {allSessionData?.data?.count === 0 ? (
              <Typography variant="h5" sx={{ p: 2 }}>
                No Records Found...
              </Typography>
            ) : (
              <>
                {allSessionData?.data?.sessions
                  ?.slice(0, 5)
                  ?.map((item: any, index: number) => (
                    <ListItem
                      key={item?.id || index}
                      component="div"
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: theme.colors.alpha.black[5],
                        },
                        borderBottom: index < 4 ? "1px solid rgba(34, 34, 34, 0.08)" : "none",
                      }}
                      onClick={() => navigate(`/sessions/upcoming-sessions/${item?.id}`)}
                    >
                      <Box sx={{ width: "100%", position: "relative" }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              item?.status === "PENDING"
                                ? "Appointment is Pending"
                                : item?.status === "PAID"
                                  ? "Payment Confirmed"
                                  : item?.status === "STARTED"
                                    ? "Appointment In Progress"
                                    : item?.status === "COMPLETED"
                                      ? "Appointment Completed"
                                      : item?.status === "CANCELLED"
                                        ? "Appointment Cancelled"
                                        : ""
                            }
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                mr: 1.5,
                                background:
                                  item?.status === "PENDING"
                                    ? theme.colors.warning.main
                                    : item?.status === "PAID"
                                      ? theme.colors.primary.main
                                      : item?.status === "STARTED"
                                        ? theme.colors.info.main
                                        : item?.status === "COMPLETED"
                                          ? theme.colors.info.main
                                          : item?.status === "CANCELLED"
                                            ? theme.colors.error.main
                                            : theme.colors.secondary.main,
                              }}
                            />
                          </Tooltip>

                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 600,
                              flex: 1,
                            }}
                          >
                            {item?.patients?.users?.fullName || item?.patientProfiles?.fullName || "Patient Name"}
                          </Typography>

                          <Button
                            size="small"
                            variant="contained"
                            sx={{
                              minWidth: "100px",
                              fontSize: "11px",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              background:
                                item?.status === "PENDING"
                                  ? theme.colors.warning.lighter
                                  : item?.status === "PAID"
                                    ? theme.colors.primary.lighter
                                    : item?.status === "STARTED"
                                      ? theme.colors.info.lighter
                                      : item?.status === "COMPLETED"
                                        ? theme.colors.info.lighter
                                        : item?.status === "CANCELLED"
                                          ? theme.colors.error.lighter
                                          : theme.colors.secondary.lighter,
                              color:
                                item?.status === "PENDING"
                                  ? theme.colors.warning.main
                                  : item?.status === "PAID"
                                    ? theme.colors.primary.main
                                    : item?.status === "STARTED"
                                      ? theme.colors.info.main
                                      : item?.status === "COMPLETED"
                                        ? theme.colors.info.main
                                        : item?.status === "CANCELLED"
                                          ? theme.colors.error.main
                                          : theme.colors.secondary.main,
                              boxShadow: "none",
                              "&:hover": {
                                background:
                                  item?.status === "PENDING"
                                    ? theme.colors.warning.light
                                    : item?.status === "PAID"
                                      ? theme.colors.primary.light
                                      : item?.status === "STARTED"
                                        ? theme.colors.info.light
                                        : item?.status === "COMPLETED"
                                          ? theme.colors.info.light
                                          : item?.status === "CANCELLED"
                                            ? theme.colors.error.light
                                            : theme.colors.secondary.light,
                                boxShadow: "none",
                              },
                            }}
                          >
                            {item?.status?.replace(/_/g, " ")}
                          </Button>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <EventIcon sx={{ fontSize: 18, color: theme.colors.alpha.black[50] }} />
                            <Typography variant="body2" sx={{ color: theme.colors.alpha.black[70], fontWeight: 500 }}>
                              {(() => {
                                let displayDate = item?.date;
                                if (!displayDate) {
                                  try {
                                    const desc = JSON.parse(item?.description || '{}');
                                    if (desc?.timeslotsByDate && desc.timeslotsByDate.length > 0) {
                                      displayDate = desc.timeslotsByDate[0].date;
                                    }
                                  } catch (e) {
                                    displayDate = new Date().toISOString().split('T')[0];
                                  }
                                }
                                return moment(displayDate).format("DD MMMM YYYY");
                              })()}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <WatchLaterIcon sx={{ fontSize: 18, color: theme.colors.alpha.black[50] }} />
                            <Typography variant="body2" sx={{ color: theme.colors.alpha.black[70], fontWeight: 600 }}>
                              {(() => {
                                const timeslots = item?.sessionTimeslots || item?.session_timeslot || item?.sessionTimeslot;

                                if (timeslots && timeslots.length > 0) {
                                  return timeslots.map((st: any, idx: number) => {
                                    const slot = st?.timeslot || st?.time_slots || st?.timeSlots;
                                    const startAt = slot?.startAt || slot?.start_at;
                                    const endAt = slot?.endAt || slot?.end_at;

                                    if (!startAt || !endAt) return null;

                                    return (
                                      <span key={idx}>
                                        {idx > 0 && ", "}
                                        {moment(startAt, "HH:mm").format("h:mm A")}-
                                        {moment(endAt, "HH:mm").format("h:mm A")}
                                      </span>
                                    );
                                  });
                                }

                                if (item?.status === "CANCELLED" || item?.status === "PENDING") {
                                  return "Time not scheduled";
                                }

                                return "Time not available";
                              })()}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {item?.sessionId && (
                            <Chip
                              size="small"
                              label={`#${item?.sessionId}`}
                              sx={{
                                backgroundColor: theme.colors.alpha.black[10],
                                color: theme.colors.alpha.black[70],
                                fontWeight: 600,
                                fontSize: "11px",
                              }}
                            />
                          )}
                          {item?.symptom && (
                            <Chip
                              size="small"
                              label={item?.symptom}
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          )}
                          {item?.consultationMode && (
                            <Chip
                              size="small"
                              label={textCapitalization(item?.consultationMode)}
                              color="secondary"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
              </>
            )}
          </Card>
        </Grid>
        <Grid item xs={3}>
          {/* <Card
            sx={{
              padding: "40px 30px 20px 30px",
            }}
          >
            <Box sx={{ textAlign: "center", margin: "auto", width: "50%" }}>
              <Avatar
                variant="rounded"
                sx={{ height: "142px", width: "142px", fontSize: "90px" }}
                src={data?.data?.imageUrl}
                alt={data?.data?.users?.fullName}
              />
              <Typography variant="h3" m="15px 0 10px 0">
                {data?.data?.users?.fullName}
              </Typography>
              <Typography variant="caption">{data?.data?.location}</Typography>
            </Box>
            <Divider sx={{ mt: 1 }} />
            <Box py="20px" display="flex" flexDirection="column" gap={1.5}>
              <ProfileBox>
                <img src={icons.graduateIcn} alt="graduate" height="16px" />
                <Typography ml={1} noWrap>
                  {data?.data?.designation}
                </Typography>
              </ProfileBox>
              <ProfileBox>
                <img src={icons.mobileIcn} alt="mobile" height="16px" />
                <Typography ml={1} noWrap>
                  {data?.data?.user?.mobileNumber}
                </Typography>
              </ProfileBox>
              <ProfileBox>
                <img src={icons.emailIcn} alt="email" height="16px" />
                <Typography ml={1} noWrap>
                  {data?.data?.user?.email}
                </Typography>
              </ProfileBox>
              <ProfileBox>
                <img src={icons.experienceIcn} alt="experience" height="16px" />
                <Typography ml={1}>
                  {data?.data?.experience} Year Experience
                </Typography>
              </ProfileBox>
              <ProfileBox>
                <img src={icons.dobIcn} alt="dob" height="16px" />
                <Typography ml={1}>
                  {moment(data?.data?.dob).format("DD-MM-YYYY")}
                </Typography>
              </ProfileBox>
              <ProfileBox>
                <img src={icons.genderIcn} alt="gender" height="16px" />
                <Typography ml={1}>
                  {textCapitalization(data?.data?.gender || "")}
                </Typography>
              </ProfileBox>
              <ProfileBox>
                <img src={icons.language} alt="gender" height="16px" />
                <Typography ml={1}>{data?.data?.languagesKnown}</Typography>
              </ProfileBox>
              <ProfileBox gap={1}>
                <img src={icons.serviceIcon} alt="gender" height="16px" />
                {data?.data?.services?.map((items: any, index: number) => (
                  <Box display="flex" key={index}>
                    <Typography>{items?.title}</Typography>
                    {data?.data?.services?.length !== index + 1 && (
                      <Typography>,</Typography>
                    )}
                  </Box>
                ))}
              </ProfileBox>
            </Box>
          </Card> */}
          <Card sx={{ padding: "28px 20px 28px 20px", marginTop: "35px" }}>
            <Typography>Overall Rating</Typography>
            <Typography mt={3} variant="h1">
              {Math.round(dashboardData?.data?.rating?.averageRating * 10) / 10}
            </Typography>
            <Rating
              readOnly
              sx={{ margin: "15px 0 20px 0" }}
              value={dashboardData?.data?.rating?.averageRating}
              precision={0.5}
            />
            <Divider />
            <Typography m="28px 0 24px 0">Total Reviews</Typography>
            <Typography variant="h1">
              {dashboardData?.data?.rating?.totalReviews}
            </Typography>
          </Card>
          <Card sx={{ padding: "28px 20px 28px 20px", marginTop: "10px" }}>
            <AvatarPrimary>
              <WatchLaterIcon />
            </AvatarPrimary>
            <Typography mt={3}>Total Reports</Typography>
            <Typography mt={2} variant="h1">
              {dashboardData?.data?.totalReports}
            </Typography>
          </Card>
          <Card
            sx={{
              padding: "8px 2px 2px 20px",
              paddingBottom: "100px",
              marginTop: "10px",
            }}
          >
            <Typography variant="h4" sx={{ mb: 2, mt: 1 }}>
              Session Analytics
            </Typography>
            <Grid container spacing={2} sx={{ marginBottom: "10px" }}>
              <Grid item lg={12}>
                <Card sx={{ width: "95%", height: "100%" }}>
                  <Box p={2} sx={{ backgroundColor: "rgba(0, 82, 204, 0.05)" }}>
                    <Typography>Sessions by Mode</Typography>
                  </Box>
                  <Box
                    width="100%"
                    height="85%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    {dashboardData?.data?.sessionModes?.every(
                      (item: any) => item.count === 0
                    ) ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="90%"
                        width="60%"
                      >
                        <Typography my={11} variant="h4">
                          No Data Available...
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={dashboardData?.data?.sessionModes}
                              dataKey="count"
                              nameKey="mode"
                              cx="50%"
                              cy="50%"
                              innerRadius={90}
                              outerRadius={110}
                              fill="color"
                            >
                              {dashboardData?.data?.sessionModes.map(
                                (entry: any, index: any) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                )
                              )}
                            </Pie>
                            <NewTip />
                          </PieChart>
                        </ResponsiveContainer>
                      </>
                    )}
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap={1.5}
                      justifyContent="center"
                      mt={2}
                      width="50%"
                      marginBottom="10%"
                    >
                      <ColorTags color="#57CA22" tag="Video" />
                      <ColorTags color="#FFA319" tag="Audio" />
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

export default TotalAnalytics;

const calculateSessionPercentageChange = (
  currentMonthSessions: any,
  previousMonthSessions: any
) => {
  const currentSessionCount = currentMonthSessions?.length;
  const previousSessionCount = previousMonthSessions?.length;

  if (previousSessionCount === 0) {
    return currentSessionCount > 0 ? "100%" : "0%";
  }

  const sessionChangePercentage =
    ((currentSessionCount - previousSessionCount) / previousSessionCount) * 100;
  return sessionChangePercentage?.toFixed(2) + "%";
};

const calculatePaymentPercentageChange = (
  currentMonthSessions: any[] | null | undefined,
  previousMonthSessions: any[] | null | undefined
) => {
  console.log(
    "[Debug] calculatePaymentPercentageChange: Input currentMonthSessions:",
    currentMonthSessions
  );
  console.log(
    "[Debug] calculatePaymentPercentageChange: Input previousMonthSessions:",
    previousMonthSessions
  );

  const currentPaymentCount = currentMonthSessions
    ? currentMonthSessions.filter((session: any, index: number) => {
      const isPaid = session.sessionPayment?.paymentStatus === true;
      return isPaid;
    }).length
    : 0;

  const previousPaymentCount = previousMonthSessions
    ? previousMonthSessions.filter(
      (session: any) => session.sessionPayment?.paymentStatus === true
    ).length
    : 0;

  if (previousPaymentCount === 0) {
    const result = currentPaymentCount > 0 ? "100%" : "0%";
    return result;
  }

  const paymentChangePercentage =
    ((currentPaymentCount - previousPaymentCount) / previousPaymentCount) * 100;

  if (isNaN(paymentChangePercentage) || !isFinite(paymentChangePercentage)) {
    return "0%";
  }

  const result = paymentChangePercentage.toFixed(2) + "%";
  return result;
};

const calculateClientPercentageChange = (
  currentMonthSessions: any,
  previousMonthSessions: any
) => {
  const currentUniqueClients = new Set(
    currentMonthSessions?.map((session: any) => session?.patientId)
  ).size;

  const previousUniqueClients = new Set(
    previousMonthSessions?.map((session: any) => session?.patientId)
  ).size;

  if (previousUniqueClients === 0) {
    return currentUniqueClients > 0 ? "100%" : "0%";
  }

  const clientChangePercentage =
    ((currentUniqueClients - previousUniqueClients) / previousUniqueClients) *
    100;
  return clientChangePercentage?.toFixed(2) + "%";
};
